import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GraduationCap, Footprints, ClipboardCheck, MessagesSquare, Radar, Check } from "lucide-react";

const MODULES = [
  { id: "train", label: "TRAIN", icon: GraduationCap, color: "#FF5722", tag: "Onboarding tracks" },
  { id: "walk", label: "WALK", icon: Footprints, color: "#38BDF8", tag: "Property inspections" },
  { id: "audit", label: "AUDIT", icon: ClipboardCheck, color: "#10B981", tag: "Compliance scoring" },
  { id: "coach", label: "COACH", icon: MessagesSquare, color: "#8B5CF6", tag: "AI 1:1 prep" },
  { id: "command", label: "COMMAND", icon: Radar, color: "#F59E0B", tag: "Portfolio radar" },
];

function TrainPanel() {
  const steps = [
    { d: "Day 30", t: "Fair-housing certified", pct: 100 },
    { d: "Day 60", t: "SOP library mastered", pct: 76 },
    { d: "Day 90", t: "Community Operations Certification", pct: 42 },
  ];
  return (
    <div className="space-y-5">
      {steps.map((s, i) => (
        <div key={s.d}>
          <div className="flex justify-between text-sm font-bold mb-1.5">
            <span className="text-white">{s.d} · <span className="text-slate-400 font-semibold">{s.t}</span></span>
            <span className="text-[#FF5722]">{s.pct}%</span>
          </div>
          <div className="h-3 rounded-full bg-white/10 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${s.pct}%` }}
              transition={{ delay: 0.2 + i * 0.15, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="h-full rounded-full bg-[#FF5722]"
            />
          </div>
        </div>
      ))}
      <div className="text-xs font-bold text-slate-500 pt-1">Every new manager runs the same playbook from day one.</div>
    </div>
  );
}

function WalkPanel() {
  const [step, setStep] = useState(0);
  const steps = [
    { n: "01", t: "Walk the site", d: "GPS + time-stamped photo documentation of every finding." },
    { n: "02", t: "Sign on the spot", d: "Resident or manager signature captured on-site." },
    { n: "03", t: "Send the PDF", d: "Branded walk report lands in inboxes before you leave the lot." },
  ];
  return (
    <div>
      <div className="flex gap-2 mb-5">
        {steps.map((s, i) => (
          <button
            key={s.n}
            data-testid={`walk-step-${i}`}
            onClick={() => setStep(i)}
            className={`flex-1 rounded-2xl border-2 px-3 py-2.5 text-xs font-bold transition-all duration-300 ${
              i <= step ? "border-[#38BDF8] bg-[#38BDF8] text-[#0B1B33]" : "border-white/15 text-slate-500"
            }`}
          >
            {i < step ? <Check className="w-3.5 h-3.5 inline mr-1" /> : null}{s.n} {s.t}
          </button>
        ))}
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -14 }}
          transition={{ duration: 0.35 }}
          className="rounded-2xl bg-[#38BDF8]/10 border-2 border-[#38BDF8]/25 p-5"
        >
          <div className="font-display font-black text-lg text-[#38BDF8]">{steps[step].t}</div>
          <p className="text-sm text-slate-300 leading-relaxed mt-1.5">{steps[step].d}</p>
        </motion.div>
      </AnimatePresence>
      {step < 2 ? (
        <button onClick={() => setStep(step + 1)} className="mt-4 text-xs font-bold text-[#38BDF8] hover:text-[#FF5722] transition-colors">
          Next step →
        </button>
      ) : (
        <div className="mt-4 text-xs font-bold text-[#10B981]">Report sent · powered by ScopeOne Walk</div>
      )}
    </div>
  );
}

function AuditPanel() {
  const rows = [
    { t: "Fair-housing compliance", s: 98 },
    { t: "Make-ready velocity", s: 84 },
    { t: "Violation closure rate", s: 91 },
    { t: "Documentation completeness", s: 73 },
  ];
  return (
    <div className="space-y-4">
      {rows.map((r, i) => (
        <div key={r.t} className="flex items-center gap-4">
          <div className="w-48 text-sm font-semibold text-slate-300 shrink-0">{r.t}</div>
          <div className="flex-1 h-3 rounded-full bg-white/10 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${r.s}%` }}
              transition={{ delay: 0.15 + i * 0.12, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="h-full rounded-full bg-[#10B981]"
            />
          </div>
          <div className="w-12 text-right font-display font-black text-[#10B981]">{r.s}</div>
        </div>
      ))}
      <div className="text-xs font-bold text-slate-500 pt-1">Corrective-action tasks auto-assigned when a score dips.</div>
    </div>
  );
}

function CoachPanel() {
  const bubbles = [
    { who: "rm", text: "Prep me for a performance conversation with my Oakridge manager." },
    { who: "ai", text: "Based on the last 3 walks: photo completion 61%, two open violations 14+ days old. Suggested framing: factual, behavior-based, with a 2-week check-in cadence. Want the full counseling draft?" },
  ];
  return (
    <div className="space-y-3">
      {bubbles.map((b, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 + i * 0.25, duration: 0.5 }}
          className={`max-w-[88%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
            b.who === "rm"
              ? "ml-auto bg-[#8B5CF6] text-white rounded-br-md"
              : "bg-white/10 border-2 border-white/10 text-slate-200 rounded-bl-md"
          }`}
        >
          {b.text}
        </motion.div>
      ))}
      <div className="text-xs font-bold text-slate-500 pt-1">Hard conversations, documented right — then finished in the Claude drafter above.</div>
    </div>
  );
}

function CommandPanel() {
  const tiles = [
    { l: "Occupancy", v: "94.2%", c: "#10B981" },
    { l: "Training completion", v: "81%", c: "#FF5722" },
    { l: "Delinquency", v: "6.1%", c: "#F59E0B" },
    { l: "Make-ready avg", v: "14 days", c: "#38BDF8" },
    { l: "Open violations", v: "23", c: "#EC4899" },
    { l: "Human velocity index", v: "A−", c: "#8B5CF6" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {tiles.map((t, i) => (
        <motion.div
          key={t.l}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 + i * 0.08, duration: 0.45 }}
          className="rounded-2xl border-2 border-white/10 bg-white/5 p-4"
        >
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
            <span className="w-1.5 h-1.5 rounded-full animate-pulse-dot" style={{ background: t.c }} /> {t.l}
          </div>
          <div className="font-display font-black text-2xl mt-1" style={{ color: t.c }}>{t.v}</div>
        </motion.div>
      ))}
    </div>
  );
}

const PANELS = { train: TrainPanel, walk: WalkPanel, audit: AuditPanel, coach: CoachPanel, command: CommandPanel };

export default function ModulePreviews() {
  const [active, setActive] = useState("train");
  const Active = PANELS[active];
  const current = MODULES.find((m) => m.id === active);

  return (
    <section id="modules" className="py-24 sm:py-32 bg-[#0B1B33]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl"
        >
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#10B981]">The Opera360 Standard</div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight mt-3 text-white">
            Five modules. One operating standard.
          </h2>
          <p className="text-lg text-slate-300 leading-relaxed mt-4">
            A live taste of the Opera360 platform your HR data flows into — tap through the modules.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12"
        >
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Opera360 modules">
            {MODULES.map((m) => {
              const Icon = m.icon;
              const isActive = m.id === active;
              return (
                <button
                  key={m.id}
                  data-testid={`module-tab-${m.id}`}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActive(m.id)}
                  className={`inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-bold border-2 transition-all duration-300 ${
                    isActive ? "text-[#0B1B33] border-transparent shadow-lg" : "border-white/15 text-slate-300 hover:border-white/40 bg-white/5"
                  }`}
                  style={isActive ? { background: m.color } : undefined}
                >
                  <Icon className="w-4 h-4" /> {m.label}
                </button>
              );
            })}
          </div>

          <div className="mt-6 rounded-3xl border-2 border-white/10 bg-[#0F172A] p-7 sm:p-10 shadow-[0_30px_80px_-25px_rgba(0,0,0,0.6)] relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: current.color }} />
            <div className="flex items-center justify-between mb-7">
              <div>
                <div className="font-display font-black text-2xl" style={{ color: current.color }}>{current.label}</div>
                <div className="text-sm font-bold text-slate-400">{current.tag}</div>
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 bg-white/5 border border-white/10 rounded-full px-3 py-1.5">
                Interactive preview · sample data
              </span>
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              >
                <Active />
              </motion.div>
            </AnimatePresence>
            <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
              <span className="text-sm font-semibold text-slate-400">This is the full platform your team trains on.</span>
              <a
                href="https://www.opera360trains.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF5722] text-white font-bold text-sm hover:bg-white hover:text-[#0B1B33] transition-colors duration-300"
              >
                Explore the full platform ↗
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
